'use client'

import React, { createContext, useState, useEffect, useContext } from 'react'
import { supabase as sharedSupabase } from '../_utils/supabase' // Import the shared supabase instance

// Create the context
const AuthRolesContext = createContext()

/**
 * Provider component for auth and roles context
 * @param {Object} props
 * @returns {React.ReactElement}
 */
export function AuthRolesProvider(props) {
    const [user, setUser] = useState(null)
    const [roles, setRoles] = useState([])
    const [subscriptionLevel, setSubscriptionLevel] = useState('basic')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [initialized, setInitialized] = useState(false)
    
    // Add debugging state
    const [debugInfo, setDebugInfo] = useState({
        authSource: null,
        directRoleCheck: null,
        directRoleCheckError: null,
        contextRefreshCount: 0
    })

    useEffect(() => {

        async function fetchUserData(user) {
            try {
                // Always fetch DB roles so subscription roles (e.g. lifetime_summit)
                // are loaded even for admin users.
                const { data: userRolesData, error: rolesError } = await sharedSupabase.rpc(
                    'get_user_roles',
                    { user_id: user.id }
                );

                let finalRoles = userRolesData || [];

                if (rolesError) {
                    console.error("Error fetching roles:", rolesError);
                    finalRoles = [];
                }

                // If the user has is_admin in metadata but that role isn't in the DB
                // result yet, inject it so hasRole('admin') still works.
                if (
                    user.user_metadata?.is_admin &&
                    !finalRoles.some((r) => r.role_name === 'admin')
                ) {
                    finalRoles = [
                        { role_name: 'admin', role_type: 'permission', expires_at: null },
                        ...finalRoles,
                    ]
                }

                setRoles(finalRoles);

                // Get subscription level
                const { data: subscriptionData, error: subError } = await sharedSupabase.rpc(
                    'get_subscription_level',
                    { user_id: user.id }
                );

                if (!subError) {
                    setSubscriptionLevel(subscriptionData);
                } else {
                    console.error("Error fetching subscription:", subError);
                    setSubscriptionLevel('basic');
                }

                setLoading(false);
            } catch (err) {
                console.error('Error in fetchUserData:', err);
                // Fallback: preserve admin from metadata, subscription unknown
                if (user?.user_metadata?.is_admin) {
                    setRoles([{
                        role_name: 'admin',
                        role_type: 'permission',
                        expires_at: null
                    }]);
                } else {
                    setRoles([]);
                }
                setSubscriptionLevel('basic');
                setLoading(false);
                setError(err.message);
            }
        }

        async function initializeAuth() {
            try {
                // Get the current session from the shared instance
                const { data: { session } } = await sharedSupabase.auth.getSession()

                if (session) {
                    console.log("📊 Initial session found:", session.user.id)
                    setUser(session.user)
                    await fetchUserData(session.user)
                } else {
                    setUser(null)
                    setRoles([])
                    setSubscriptionLevel('basic')
                    setLoading(false)
                }
                
                setInitialized(true)
            } catch (err) {
                console.error('Error initializing auth:', err)
                setError(err.message)
                setLoading(false)
                setInitialized(true)
            }
        }

        // Initialize
        initializeAuth()

        // Subscribe to auth changes using the shared instance
        const {
            data: { subscription },
        } = sharedSupabase.auth.onAuthStateChange(async (event, session) => {
            
            if (event === 'SIGNED_IN' && session) {
                setUser(session.user)
                setLoading(true) // Set loading true before fetching
                await fetchUserData(session.user)
                
                setDebugInfo(prev => ({ 
                    ...prev, 
                    authSource: 'onAuthStateChange:SIGNED_IN',
                    contextRefreshCount: prev.contextRefreshCount + 1
                }))
            } else if (event === 'SIGNED_OUT') {
                setUser(null)
                setRoles([])
                setSubscriptionLevel('basic')
                setLoading(false)
                
                setDebugInfo(prev => ({ 
                    ...prev, 
                    authSource: 'onAuthStateChange:SIGNED_OUT',
                    contextRefreshCount: prev.contextRefreshCount + 1
                }))
            } else if (event === 'TOKEN_REFRESHED' && session) {
                // Handle token refresh to update roles
                console.log("📊 Token refreshed, updating user data")
                setLoading(true) // Set loading true before fetching
                await fetchUserData(session.user)
                
                setDebugInfo(prev => ({ 
                    ...prev, 
                    authSource: 'onAuthStateChange:TOKEN_REFRESHED',
                    contextRefreshCount: prev.contextRefreshCount + 1
                }))
            }
        })

        // Clean up subscription on unmount
        return () => {
            subscription.unsubscribe()
        }
    }, [])

    // Check if a user has a specific role
    const hasRole = (roleName) => {
        // Check for admin in metadata first as a quick path
        if (roleName === 'admin' && user?.user_metadata?.is_admin) {
            console.log(`📊 Admin found in metadata`);
            return true;
        }
        
        // Then check roles from context
        if (!user || !roles.length) return false;
        const result = roles.some((role) => role.role_name === roleName);
        console.log(`📊 Checking role ${roleName}:`, result, roles);
        return result;
    }

    // Check if a user has a minimum subscription level
    const hasMinSubscription = (level) => {
        const levels = {
            basic: 0,
            turbo: 1,
            summit: 2,
            lifetime_turbo: 3,
            lifetime_summit: 4,
        }

        const userLevel = levels[subscriptionLevel] || 0
        const requiredLevel = levels[level] || 0

        return userLevel >= requiredLevel
    }

    // Context value
    const value = {
        user,
        roles,
        subscriptionLevel,
        loading,
        error,
        initialized,
        hasRole,
        hasMinSubscription,
        debugInfo, // Add debug info to context for access in components
    }

    // Render the provider with the value
    return React.createElement(
        AuthRolesContext.Provider,
        { value },
        props.children
    )
}

/**
 * Hook to use the auth roles context
 * @returns {Object} The auth roles context
 */
export function useAuthRoles() {
    const context = useContext(AuthRolesContext)
    if (context === undefined) {
        throw new Error('useAuthRoles must be used within an AuthRolesProvider')
    }
    return context
}
